import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, MegaphoneIcon } from "@heroicons/react/24/outline";
import { api } from "../utils/api";

const PublicTalksUpcomingCard = ({ disabled = false }) => {
  const [entries, setEntries] = useState([]);
  const [monthIndex, setMonthIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (disabled) {
      setEntries([]);
      return undefined;
    }
    api
      .get("/discorsi-pubblici/upcoming")
      .then((response) => {
        if (!cancelled) {
          setEntries(response.data?.entries || []);
          setMonthIndex(0);
        }
      })
      .catch(() => {
        if (!cancelled) setEntries([]);
      });
    return () => {
      cancelled = true;
    };
  }, [disabled]);

  const entriesByMonth = useMemo(() => entries.reduce((groups, entry) => {
    const key = String(entry.data).slice(0, 7);
    groups[key] = [...(groups[key] || []), entry];
    return groups;
  }, {}), [entries]);
  const monthKeys = Object.keys(entriesByMonth).sort();
  const visibleMonthKey = monthKeys[monthIndex] || monthKeys[0];
  const visibleEntries = entriesByMonth[visibleMonthKey] || [];
  const visibleMonthLabel = visibleMonthKey
    ? new Intl.DateTimeFormat("it-IT", { month: "long", year: "numeric" })
      .format(new Date(`${visibleMonthKey}-15T12:00:00`))
    : "";

  if (!entries.length) return null;

  return (
    <section className="bg-white border border-blue-100 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
        <span className="h-10 w-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center">
          <MegaphoneIcon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-semibold text-gray-900">Prossimi discorsi pubblici</h2>
          <p className="text-sm text-gray-500">Programma mensile della congregazione</p>
        </div>
        {monthKeys.length > 1 && <div className="flex items-center gap-1">
          <button type="button" onClick={() => setMonthIndex((current) => Math.max(0, current - 1))} disabled={monthIndex === 0} className="rounded-md p-1.5 text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Mese precedente">
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <button type="button" onClick={() => setMonthIndex((current) => Math.min(monthKeys.length - 1, current + 1))} disabled={monthIndex === monthKeys.length - 1} className="rounded-md p-1.5 text-gray-600 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40" aria-label="Mese successivo">
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>}
      </div>
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-2">
        <span className="text-sm font-semibold capitalize text-gray-800">{visibleMonthLabel}</span>
        <span className="text-xs text-gray-500">{visibleEntries.length} {visibleEntries.length === 1 ? "discorso" : "discorsi"}</span>
      </div>
      <div className="divide-y divide-gray-100">
        {visibleEntries.map((entry) => (
          <div key={entry.id} className={`px-5 py-4 grid gap-1 md:grid-cols-[170px_1fr] ${entry.is_mine ? "bg-blue-50" : ""}`}>
            <div className="text-sm font-medium text-gray-800">
              {new Date(`${String(entry.data).slice(0, 10)}T12:00:00`).toLocaleDateString("it-IT", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
            <div>
              <div className="font-medium text-gray-900">
                {entry.nome} {entry.cognome}
                <span className="font-normal text-gray-500"> · {entry.congregazione_origine}</span>
                {entry.is_mine && <span className="ml-2 text-xs rounded-full bg-blue-100 text-blue-800 px-2 py-1">Il tuo incarico</span>}
              </div>
              <div className="text-sm text-gray-600">
                Schema {entry.numero}: {entry.titolo}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default PublicTalksUpcomingCard;
